import { jsx as _jsx } from "react/jsx-runtime";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { forwardRef } from "react";
import { cn } from "../lib/cn";
/**
 * Popover — the anchored panel primitive.
 *
 * Radix owns the hard parts we would otherwise get wrong: collision-aware
 * placement, outside-click and Escape dismissal, focus return to the trigger,
 * and marking the rest of the page inert-ish while open. Do not fight it.
 *
 * This wrapper exists only to pin the panel's SKIN (token surface, border,
 * radius, shadow) and its STACKING to the same values `SelectContent` uses, so
 * a popover and a select popup opened from the same form look like one system.
 *
 * Every user-facing string inside a popover arrives from the consuming app,
 * already translated — `packages/ui` never imports `@instanexus/i18n`.
 */
export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;
export const PopoverClose = PopoverPrimitive.Close;
/**
 * Callback ref for a `PopoverContent` whose list must scroll by wheel (and a
 * touch drag) when the popover is opened from inside a `Dialog`.
 *
 * The popup is portaled to `document.body`, OUTSIDE the Dialog's content, and
 * the Dialog's `react-remove-scroll` lock listens for `wheel` / `touchmove` on
 * `document` and cancels every one whose target is outside its own node — so a
 * portaled list could be dragged by its scrollbar but never wheeled. Stopping
 * those two events at the popup means the lock's document listener never sees
 * them and the browser scrolls the list natively (`overscroll-contain` on the
 * list keeps it from chaining to the page underneath).
 *
 * Chosen over `<Popover modal>`, which would also fix the wheel (a modal
 * popover mounts its own, newer lock), because modal changes what a click
 * outside does: it disables pointer events on everything else, so clicking the
 * next field while the list is open would only close the list instead of also
 * landing on that field. Native listeners, not React `onWheel`, so the stop
 * does not depend on where React attaches its own root listener for a portal.
 *
 * A module-level function, so the ref identity is stable across renders and
 * every consumer (`SearchableSelect`, `SearchableMultiSelect`) shares ONE
 * implementation. React 19 runs the returned cleanup when the node detaches.
 */
export function scrollableInDialog(node) {
    if (!node)
        return undefined;
    const stop = (event) => {
        event.stopPropagation();
    };
    node.addEventListener("wheel", stop, { passive: true });
    node.addEventListener("touchmove", stop, { passive: true });
    return () => {
        node.removeEventListener("wheel", stop);
        node.removeEventListener("touchmove", stop);
    };
}
export const PopoverContent = forwardRef(function PopoverContent({ className, align = "start", sideOffset = 4, ...props }, ref) {
    return (_jsx(PopoverPrimitive.Portal, { children: _jsx(PopoverPrimitive.Content, { ref: ref, align: align, sideOffset: sideOffset, 
            // `--z-popover`, for the same reason `SelectContent` uses it: this content
            // is portaled to `document.body`, so inside a `Dialog` it is a SIBLING of
            // the overlay (`--z-overlay`) rather than a child. Anything lower renders
            // behind the scrim and is unreachable.
            style: { zIndex: "var(--z-popover)", ...props.style }, className: cn("rounded-card border border-border bg-surface-raised text-primary shadow-popover", "outline-none", className), ...props }) }));
});
PopoverContent.displayName = PopoverPrimitive.Content.displayName;
//# sourceMappingURL=popover.js.map